import Chart from './Chart'
import { OUTCOME_COLORS, donutOption } from './chartTheme'
import { kit } from './strings'
import './OutcomeShare.css'
import { useTheme } from '../lib/theme'

const KEYS = Object.keys(OUTCOME_COLORS)

/**
 * The four programme outcome shares — pass / fail / incomplete / absent.
 *
 * A ring with the headline figure in its middle, and a key beside it.
 *
 * It has been three things now, and each change fixed the one before it. Four
 * separate 0–100 dials said the figures were four unrelated readings when they
 * are four parts of one hundred percent. A flat 100% bar said that correctly —
 * but it sits at the head of a card whose main plot is also horizontal bars, so
 * the card ended up making its point twice in the same shape. The ring says the
 * same thing in a form nothing else on the page uses, and its middle is free for
 * the number the whole card is about: how many passed.
 *
 * Colours come from OUTCOME_COLORS rather than from CSS, so the ring, the
 * stacked plot beneath it and the table all take their tones from one
 * definition.
 *
 * @param {{success: number, failure: number, incomplete: number, absent: number}} shares
 * @param {Record<string, string>} labels  keyed by outcome
 * @param {Record<string, string>} [notes] optional per-outcome tooltip
 * @param {'card'|'inset'} [variant]  `card` stands alone above a list; `inset`
 *                                    sits inside a panel that already has a surface
 */
export default function OutcomeShare({ shares, labels, notes = {}, variant = 'card' }) {
  /* The donut bakes resolved colours into its option — see useTheme. */
  useTheme()
  if (!shares) return null

  return (
    <div className={`outcome-share outcome-share--${variant}`}>
      <div className="outcome-share__ring">
        <Chart
          option={donutOption(shares, labels)}
          height={132}
          ariaLabel={KEYS.map((key) => `${labels[key]} ${shares[key]}%`).join(kit.listSep)}
        />
        {/* Real text over the canvas, not an ECharts label: the middle of a
            donut is the one place a figure has to be set in the page's own
            Arabic type and tabular numerals. */}
        <span className="outcome-share__centre" aria-hidden="true">
          <b className="outcome-share__centre-value" dir="ltr">
            {shares.success}%
          </b>
          <bdi className="outcome-share__centre-label">{labels.success}</bdi>
        </span>
      </div>

      <ul className="outcome-share__keys">
        {KEYS.map((key) => (
          <li key={key} className="outcome-share__key" title={notes[key] ?? undefined}>
            <i
              className="outcome-share__dot"
              style={{ background: OUTCOME_COLORS[key] }}
              aria-hidden="true"
            />
            {/* Figure before its label, and adjacent to it. Ranged to opposite
                ends of a wide cell they read across the gap instead of down it,
                and every percentage looked like it belonged to the entry beside
                it. */}
            <b className="outcome-share__value" dir="ltr">
              {shares[key]}%
            </b>
            <bdi className="outcome-share__label">{labels[key]}</bdi>
          </li>
        ))}
      </ul>
    </div>
  )
}
